import type { CasePriority, CaseSeverity, CaseStatus, RunItemStatus, RunStatus } from '@tms/shared';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronsUp,
  Circle,
  Equal,
  Loader,
  Minus,
  MinusCircle,
  OctagonAlert,
  SkipForward,
  XCircle,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useEnumLabels, useT } from '../../i18n';
import { cn } from '../../lib/cn';

export const Badge = ({
  children,
  className,
  title,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
}) => (
  <span
    title={title}
    className={cn(
      'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] font-medium',
      'bg-sunken text-ink-muted',
      className,
    )}
  >
    {children}
  </span>
);

const RUN_ITEM_STYLES: Record<RunItemStatus, string> = {
  not_run: 'bg-notrun-soft text-notrun',
  passed: 'bg-pass-soft text-pass',
  failed: 'bg-fail-soft text-fail',
  blocked: 'bg-block-soft text-block',
  skipped: 'bg-skip-soft text-skip',
};

/**
 * Result statuses never rely on colour alone: each carries its own icon and its
 * label, so they stay readable for colour-blind users and in screenshots.
 */
export const RUN_ITEM_STATUS_ICONS: Record<RunItemStatus, typeof Circle> = {
  not_run: Circle,
  passed: CheckCircle2,
  failed: XCircle,
  blocked: AlertTriangle,
  skipped: MinusCircle,
};

export const RunItemStatusIcon = ({
  status,
  className,
}: {
  status: RunItemStatus;
  className?: string;
}) => {
  const Icon = RUN_ITEM_STATUS_ICONS[status];
  return <Icon className={cn('size-4 shrink-0', className)} aria-hidden="true" />;
};

export const RunItemStatusBadge = ({ status }: { status: RunItemStatus }) => {
  const { itemStatus } = useEnumLabels();
  return (
    <Badge className={RUN_ITEM_STYLES[status]}>
      <RunItemStatusIcon status={status} className="size-3" />
      {itemStatus(status)}
    </Badge>
  );
};

const RUN_STYLES: Record<RunStatus, string> = {
  active: 'bg-accent-soft text-accent-fg',
  completed: 'bg-pass-soft text-pass',
  aborted: 'bg-fail-soft text-fail',
};

const RUN_ICONS: Record<RunStatus, typeof Circle> = {
  active: Loader,
  completed: CheckCircle2,
  aborted: SkipForward,
};

export const RunStatusBadge = ({ status }: { status: RunStatus }) => {
  const { runStatus } = useEnumLabels();
  const Icon = RUN_ICONS[status];
  return (
    <Badge className={RUN_STYLES[status]}>
      <Icon className="size-3 shrink-0" aria-hidden="true" />
      {runStatus(status)}
    </Badge>
  );
};

/**
 * Priority and severity read as a rank, so each value gets a direction (the
 * arrow) and a temperature (the colour) — the pattern issue trackers use. The
 * label is always present: colour and icon are the fast path, not the only one.
 */
const PRIORITY_META: Record<CasePriority, { icon: typeof Circle; className: string }> = {
  low: { icon: ChevronDown, className: 'bg-pass-soft text-pass' },
  medium: { icon: Equal, className: 'bg-sunken text-ink-muted' },
  high: { icon: ChevronUp, className: 'bg-block-soft text-block' },
  critical: { icon: ChevronsUp, className: 'bg-fail-soft text-fail' },
};

export const PriorityIcon = ({
  priority,
  className,
}: {
  priority: CasePriority;
  className?: string;
}) => {
  const Icon = PRIORITY_META[priority].icon;
  return <Icon className={cn('size-3 shrink-0', className)} aria-hidden="true" />;
};

export const PriorityBadge = ({ priority }: { priority: CasePriority }) => {
  const { t } = useT();
  const { priority: label } = useEnumLabels();
  return (
    <Badge className={PRIORITY_META[priority].className} title={t('enum.priorityLabel')}>
      <PriorityIcon priority={priority} />
      {label(priority)}
    </Badge>
  );
};

const SEVERITY_META: Record<CaseSeverity, { icon: typeof Circle; className: string }> = {
  trivial: { icon: Minus, className: 'bg-sunken text-ink-subtle' },
  minor: { icon: ArrowDown, className: 'bg-sunken text-ink-muted' },
  major: { icon: ArrowUp, className: 'bg-block-soft text-block' },
  critical: { icon: AlertTriangle, className: 'bg-fail-soft text-fail' },
  blocker: { icon: OctagonAlert, className: 'bg-fail text-white' },
};

export const SeverityIcon = ({
  severity,
  className,
}: {
  severity: CaseSeverity;
  className?: string;
}) => {
  const Icon = SEVERITY_META[severity].icon;
  return <Icon className={cn('size-3 shrink-0', className)} aria-hidden="true" />;
};

export const SeverityBadge = ({ severity }: { severity: CaseSeverity }) => {
  const { t } = useT();
  const { severity: label } = useEnumLabels();
  return (
    <Badge className={SEVERITY_META[severity].className} title={t('enum.severityLabel')}>
      <SeverityIcon severity={severity} />
      {label(severity)}
    </Badge>
  );
};

const CASE_STATUS_STYLES: Record<CaseStatus, string> = {
  draft: 'bg-sunken text-ink-subtle',
  active: 'bg-pass-soft text-pass',
  deprecated: 'bg-fail-soft text-fail',
};

export const CaseStatusBadge = ({ status }: { status: CaseStatus }) => {
  const { caseStatus } = useEnumLabels();
  return <Badge className={CASE_STATUS_STYLES[status]}>{caseStatus(status)}</Badge>;
};

/** Monospaced display key (PAY-12), the identifier users actually quote. */
export const EntityKey = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span className={cn('font-mono text-[12px] text-ink-subtle', className)}>{children}</span>
);
